import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { getWebSocketConfig, WebSocketMessage, WebSocketMessageAction } from '@/config/websocket.config'

import { baseService } from './base.service'
import { IBaseService } from './interfaces/base-service.interface'

import {
  ChatNotification,
  ChatRoomJoin,
  Conversation,
  ConversationParticipant,
  CreateConversationDto,
  CreateMessageDto,
  CreateMessageReactionDto,
  CreateReplyMessageDto,
  DeleteMessageDto,
  DeleteMessageReactionDto,
  GetUnreadCountsResponse,
  Message,
  MessageKeysetListResponse,
  MessageReaction,
  MessageType,
  PinnedMessage,
  TypingIndicator,
  UpdateConversationDto,
  UpdateMessageDto,
  UpdateParticipantRoleDto,
} from '@/types/chat.type'

export class GeneralChatService {
  private static instance: GeneralChatService
  private readonly api: AxiosInstance
  private socket: WebSocket | null = null
  private messageQueue: WebSocketMessage[] = []
  private isConnected = false
  private reconnectAttempts = 0
  private heartbeatInterval: NodeJS.Timeout | null = null
  private reconnectTimeout: NodeJS.Timeout | null = null

  // Event listeners
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private eventListeners: Map<string, Set<(data: any) => void>> = new Map()

  constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): GeneralChatService {
    if (!GeneralChatService.instance) {
      GeneralChatService.instance = new GeneralChatService(baseService)
    }
    return GeneralChatService.instance
  }

  // ===== REST API Methods =====

  /**
   * Create a new conversation
   */
  public async createConversation(data: CreateConversationDto, configs?: AxiosRequestConfig): Promise<Conversation> {
    const { data: responseData } = await this.api.post<Conversation>('/chat/conversations', data, configs)
    return responseData
  }

  /**
   * Get all conversations for the current user
   */
  public async getConversations(configs?: AxiosRequestConfig): Promise<Conversation[]> {
    const { data } = await this.api.get<Conversation[]>('/chat/conversations', configs)
    return data
  }

  /**
   * Update a conversation
   */
  public async updateConversation(
    id: number,
    data: UpdateConversationDto,
    configs?: AxiosRequestConfig
  ): Promise<Conversation> {
    const { data: responseData } = await this.api.put<Conversation>(`/chat/conversations/${id}`, data, configs)
    return responseData
  }

  /**
   * Delete a conversation - soft delete (default) or permanent delete
   * @param id - Conversation ID
   * @param permanent - If true, permanently deletes the conversation. If false, soft deletes it for the current user only
   * @param configs - Additional axios request config
   */
  public async deleteConversation(id: number, permanent = false, configs?: AxiosRequestConfig): Promise<void> {
    await this.api.delete(`/chat/conversations/${id}/delete`, {
      ...configs,
      params: {
        ...configs?.params,
        permanent: permanent.toString(),
      },
    })
  }

  /**
   * Create a new message
   */
  public async createMessage(data: CreateMessageDto, configs?: AxiosRequestConfig): Promise<Message> {
    const { data: responseData } = await this.api.post<Message>('/chat/messages', data, configs)
    return responseData
  }

  /**
   * Create a reply message
   */
  public async createReplyMessage(data: CreateReplyMessageDto, configs?: AxiosRequestConfig): Promise<Message> {
    const { data: responseData } = await this.api.post<Message>('/chat/messages/reply', data, configs)
    return responseData
  }

  public async createMessageWithFile(
    data: CreateMessageDto & { file: File },
    configs?: AxiosRequestConfig
  ): Promise<Message> {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('conversationId', data.conversationId.toString())
    formData.append('content', data.content || '')
    formData.append('metadata', JSON.stringify(data.metadata))
    if (data.taggedUserIds && data.taggedUserIds.length > 0) {
      formData.append('taggedUserIds', JSON.stringify(data.taggedUserIds))
    }

    const { data: responseData } = await this.api.post<Message>('/chat/messages/file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...configs,
    })
    return responseData
  }

  /**
   * Get messages for a conversation with keyset pagination.
   * @see docs/features/chat/message-keyset-pagination.md
   */
  public async getMessages(
    conversationId: number,
    options: {
      limit?: number
      cursor?: string
      direction?: 'before' | 'after'
      beforeCursor?: string
      afterCursor?: string
      types?: MessageType[]
    } = {},
    configs?: AxiosRequestConfig
  ): Promise<MessageKeysetListResponse> {
    const params: Record<string, string | number> = {
      limit: options.limit ?? 50,
    }

    if (options.cursor) {
      params.cursor = options.cursor
    }
    if (options.direction) {
      params.direction = options.direction
    }
    if (options.beforeCursor) {
      params.beforeCursor = options.beforeCursor
    }
    if (options.afterCursor) {
      params.afterCursor = options.afterCursor
    }
    if (options.types?.length) {
      params.types = options.types.join(',')
    }

    const { data } = await this.api.get<MessageKeysetListResponse>(
      `/chat/conversations/${conversationId}/messages`,
      {
        params,
        ...configs,
      }
    )
    return data
  }

  /**
   * Get a specific message with context (keyset pagination).
   */
  public async getMessageWithContext(
    messageId: number,
    options?: {
      limit?: number
      beforeCursor?: string
    },
    configs?: AxiosRequestConfig
  ): Promise<MessageKeysetListResponse> {
    const params: Record<string, string | number> = {}

    if (options?.limit) {
      params.limit = options.limit
    }
    if (options?.beforeCursor) {
      params.beforeCursor = options.beforeCursor
    }

    const { data } = await this.api.get<MessageKeysetListResponse>(`/chat/messages/${messageId}`, {
      params,
      ...configs,
    })
    return data
  }

  /**
   * Update a message
   */
  public async updateMessage(id: number, data: UpdateMessageDto, configs?: AxiosRequestConfig): Promise<Message> {
    const { data: responseData } = await this.api.put<Message>(`/chat/messages/${id}`, data, configs)
    return responseData
  }

  /**
   * Delete a message
   */
  public async deleteMessage(
    id: number,
    deleteMessageDto: DeleteMessageDto,
    configs?: AxiosRequestConfig
  ): Promise<void> {
    await this.api.delete(`/chat/messages/${id}`, { data: deleteMessageDto, ...configs })
  }

  /**
   * Mark a conversation as read
   */
  public async markConversationAsRead(conversationId: number, configs?: AxiosRequestConfig): Promise<void> {
    await this.api.post(`/chat/conversations/${conversationId}/read`, {}, configs)
  }

  /**
   * Send typing indicator state for a conversation
   */
  public async sendTypingIndicatorState(
    conversationId: number,
    userId: number,
    isTyping: boolean,
    userName: string,
    participantUserIds: number[],
    configs?: AxiosRequestConfig
  ): Promise<void> {
    await this.api.post(
      `/chat/conversations/${conversationId}/typing`,
      { userId, isTyping, userName, participantUserIds },
      configs
    )
  }

  /**
   * Get unread counts for multiple conversations
   */
  public async getUnreadCounts(
    conversationIds: number[],
    configs?: AxiosRequestConfig
  ): Promise<GetUnreadCountsResponse> {
    const { data } = await this.api.post<GetUnreadCountsResponse>(
      '/chat/conversations/unread-counts',
      {
        conversationIds,
      },
      configs
    )
    return data
  }

  /**
   * Search conversations
   */
  public async searchConversations(query: string, configs?: AxiosRequestConfig): Promise<Conversation[]> {
    const { data } = await this.api.get<Conversation[]>('/chat/conversations/search', {
      params: { q: query },
      ...configs,
    })
    return data
  }

  /**
   * Search messages globally across all conversations
   */
  public async searchMessages(
    query: string,
    options?: { limit?: number; cursor?: string },
    configs?: AxiosRequestConfig
  ): Promise<MessageKeysetListResponse> {
    const { data } = await this.api.get<MessageKeysetListResponse>('/chat/messages/search', {
      params: {
        q: query,
        ...(options?.limit ? { limit: options.limit } : {}),
        ...(options?.cursor ? { cursor: options.cursor } : {}),
      },
      ...configs,
    })
    return data
  }

  /**
   * Add participants to a conversation
   */
  public async addParticipants(
    conversationId: number,
    participantIds: number[],
    configs?: AxiosRequestConfig
  ): Promise<void> {
    await this.api.post(`/chat/conversations/${conversationId}/participants`, { participantIds }, configs)
  }

  /**
   * Remove participants from a conversation
   */
  public async removeParticipants(
    conversationId: number,
    participantIds: number[],
    configs?: AxiosRequestConfig
  ): Promise<void> {
    await this.api.delete(`/chat/conversations/${conversationId}/participants`, {
      data: { participantIds },
      ...configs,
    })
  }

  /**
   * Leave a conversation (for the current user)
   */
  public async leaveConversation(conversationId: number, configs?: AxiosRequestConfig): Promise<void> {
    await this.api.post(`/chat/conversations/${conversationId}/leave`, {}, configs)
  }

  /**
   * Update participant role in a conversation
   */
  public async updateParticipantRole(
    data: UpdateParticipantRoleDto,
    configs?: AxiosRequestConfig
  ): Promise<ConversationParticipant> {
    const { data: responseData } = await this.api.put<ConversationParticipant>(
      `/chat/conversations/${data.conversationId}/participants/${data.participantId}/role`,
      { newRole: data.newRole },
      configs
    )
    return responseData
  }

  // ===== Message Reaction Methods =====

  /**
   * Create or toggle a reaction on a message
   */
  public async createMessageReaction(
    reactionDto: CreateMessageReactionDto,
    configs?: AxiosRequestConfig
  ): Promise<MessageReaction> {
    const { data } = await this.api.post<MessageReaction>('/message-reactions', reactionDto, configs)
    return data
  }

  /**
   * Remove a reaction from a message
   */
  public async deleteMessageReaction(
    deleteReactionDto: DeleteMessageReactionDto,
    configs?: AxiosRequestConfig
  ): Promise<void> {
    await this.api.delete('/message-reactions', { data: deleteReactionDto, ...configs })
  }

  // ===== Pin/Unpin Conversation Methods =====

  /**
   * Pin a conversation
   * @param conversationId Conversation ID to pin
   * @param configs Optional axios config
   * @returns Promise<void>
   */
  public async pinConversation(conversationId: number, configs?: AxiosRequestConfig): Promise<void> {
    await this.api.post(`/chat/conversations/${conversationId}/pin`, {}, configs)
  }

  /**
   * Unpin a conversation
   * @param conversationId Conversation ID to unpin
   * @param configs Optional axios config
   * @returns Promise<void>
   */
  public async unpinConversation(conversationId: number, configs?: AxiosRequestConfig): Promise<void> {
    await this.api.post(`/chat/conversations/${conversationId}/unpin`, {}, configs)
  }

  // ===== Pin/Unpin Message Methods =====

  /**
   * Pin a message in a conversation
   * @param conversationId Conversation ID
   * @param messageId Message ID to pin
   * @param configs Optional axios config
   * @returns Promise<PinnedMessage>
   */
  public async pinMessage(
    conversationId: number,
    messageId: number,
    configs?: AxiosRequestConfig
  ): Promise<PinnedMessage> {
    const { data } = await this.api.post(`/chat/conversations/${conversationId}/messages/${messageId}/pin`, {}, configs)
    return data
  }

  /**
   * Unpin a message in a conversation
   * @param conversationId Conversation ID
   * @param messageId Message ID to unpin
   * @param configs Optional axios config
   * @returns Promise<void>
   */
  public async unpinMessage(conversationId: number, messageId: number, configs?: AxiosRequestConfig): Promise<void> {
    await this.api.post(`/chat/conversations/${conversationId}/messages/${messageId}/unpin`, {}, configs)
  }

  /**
   * Get all pinned messages in a conversation
   * @param conversationId Conversation ID
   * @param configs Optional axios config
   * @returns Promise<Message[]>
   */
  public async getPinnedMessages(conversationId: number, configs?: AxiosRequestConfig): Promise<PinnedMessage[]> {
    const { data } = await this.api.get<PinnedMessage[]>(
      `/chat/conversations/${conversationId}/pinned-messages`,
      configs
    )
    return data
  }

  // ===== WebSocket Methods =====

  /**
   * Initialize WebSocket connection
   */
  public initialize(): WebSocket {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return this.socket
    }

    const hostname = typeof window !== 'undefined' ? window.location.hostname : null
    const config = getWebSocketConfig(hostname)
    this.socket = new WebSocket(`${config.apiGatewayUrl}chat`)

    this.socket.onopen = () => {
      this.isConnected = true
      this.reconnectAttempts = 0
      this.flushMessageQueue()
      this.startHeartbeat()
      this.emit('connected', {})
    }

    this.socket.onclose = event => {
      this.isConnected = false
      this.stopHeartbeat()
      this.emit('disconnected', { code: event.code, reason: event.reason })

      // Attempt to reconnect if not a clean close
      if (event.code !== 1000 && this.reconnectAttempts < config.maxReconnectAttempts) {
        this.scheduleReconnect()
      }
    }

    this.socket.onerror = error => {
      // eslint-disable-next-line no-console
      console.error('WebSocket error:', error)
      this.emit('error', error)
    }

    this.socket.onmessage = event => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data)
        this.handleWebSocketMessage(message)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    return this.socket
  }

  /**
   * Get the current WebSocket instance
   */
  public getSocket(): WebSocket | null {
    return this.socket
  }

  /**
   * Get connection status
   */
  public getConnectionStatus(): boolean {
    return this.isConnected
  }

  /**
   * Disconnect WebSocket
   */
  public disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.socket) {
      this.socket.close(1000, 'User initiated disconnect')
      this.socket = null
    }

    this.isConnected = false
    this.stopHeartbeat()
  }

  /**
   * Join a chat room
   */
  public joinChatRoom(data: ChatRoomJoin): void {
    const message: WebSocketMessage = {
      action: WebSocketMessageAction.SUBSCRIBE,
      data: {
        ...data,
        channel: `chat-${data.conversationId}`,
      },
    }
    this.sendMessage(message)
  }

  /**
   * Send typing indicator
   */
  public sendTypingIndicator(data: TypingIndicator): void {
    const message: WebSocketMessage = {
      action: 'typing_indicator',
      data: {
        ...data,
        channel: `typing-${data.conversationId}`,
      },
    }
    this.sendMessage(message)
  }

  // ===== Event System =====

  /**
   * Add event listener
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public on(event: string, callback: (data: any) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.add(callback)
    }
  }

  /**
   * Remove event listener
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public off(event: string, callback: (data: any) => void): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.delete(callback)
      if (listeners.size === 0) {
        this.eventListeners.delete(event)
      }
    }
  }

  /**
   * Emit event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error in event listener for ${event}:`, error)
        }
      })
    }
  }

  // ===== Private Methods =====

  /**
   * Send a WebSocket message
   */
  private sendMessage(message: WebSocketMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message))
    } else {
      this.queueMessage(message)
    }
  }

  /**
   * Queue a message for later sending
   */
  private queueMessage(message: WebSocketMessage): void {
    this.messageQueue.push(message)
  }

  /**
   * Flush queued messages
   */
  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift()
      if (message && this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify(message))
      }
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleWebSocketMessage(message: WebSocketMessage): void {
    switch (message.action) {
      case WebSocketMessageAction.NOTIFICATION:
        this.handleNotification(message.data as ChatNotification)
        break
      case WebSocketMessageAction.PONG:
        // Heartbeat response
        break
      case WebSocketMessageAction.ERROR:
        this.emit('error', message.data)
        break
      default:
        this.emit(message.action, message.data)
    }
  }

  /**
   * Handle chat notifications
   */
  private handleNotification(notification: ChatNotification): void {
    this.emit(notification.type, notification.data)
  }

  /**
   * Start heartbeat
   */
  private startHeartbeat(): void {
    const config = getWebSocketConfig()
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        const message: WebSocketMessage = {
          action: WebSocketMessageAction.PING,
          timestamp: Date.now(),
        }
        this.socket.send(JSON.stringify(message))
      }
    }, config.heartbeatInterval)
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  /**
   * Schedule reconnection
   */
  private scheduleReconnect(): void {
    const config = getWebSocketConfig()
    this.reconnectAttempts++

    this.reconnectTimeout = setTimeout(() => {
      this.emit('reconnecting', { attempt: this.reconnectAttempts })
      this.initialize()
    }, config.reconnectInterval * this.reconnectAttempts)
  }
}

export const generalChatService = GeneralChatService.getInstance(baseService)
